import { snowflakeId, coerceDateTimeLocal } from '../journey-accommodation/accommodationModel';
import { coerceDateTime } from '../my-journeys/myJourneysModel';
import { memberDisplayName } from '../member-display/memberDisplayModel';
import {
  formatMoneyDisplay,
  formatSignedMoneyDisplay,
  moneyToCanonicalString,
  moneyToCanonicalStringSigned,
  netBalanceKind,
  netBalanceLabel,
} from '../finance/financeMoney';

export const SPLIT_STATUS = Object.freeze({
  CURRENT: 'CURRENT',
  OUTDATED: 'OUTDATED',
});

export const SPLIT_STATUS_LABELS = Object.freeze({
  CURRENT: '分帳結果為最新',
  OUTDATED: '支出已變更，需要重新計算',
});

export function splitStatusLabel(status) {
  const key = String(status || '');
  return SPLIT_STATUS_LABELS[key] || key || '—';
}

export function classifySplitError(cause) {
  const code = cause?.code ?? cause?.raw?.code;
  const key = String(cause?.raw?.key || cause?.message || '');
  const message = String(cause?.raw?.message || cause?.message || '');
  if (code === 90003 || code === 90005 || code === 90006
      || key.includes('token.not.found') || key.includes('token.illegal') || key.includes('token.expired')) {
    return 'unauthorized';
  }
  if (code === 92001 || key === 'travel.journey.not.found') return 'journey-not-found';
  if (code === 93001 && key.includes('budget.not.found')) return 'budget-not-found';
  if (code === 93001 || key.includes('planning.not.found') || key.includes('SplitCalculationNotFound')) {
    return 'split-not-found';
  }
  if (code === 92002 || code === 93002 || key.includes('permission.denied')) return 'forbidden';
  if (code === 93003 && key.includes('concurrent.modification')) return 'conflict';
  if (code === 93003 && key.includes('lifecycle')) return 'lifecycle';
  if (code === 93003 && (key.includes('idempotency')
      || message.toLowerCase().includes('clientrequestid')
      || message.toLowerCase().includes('payload conflict'))) {
    return 'idempotency-conflict';
  }
  if (code === 93003 && key === 'travel.planning.conflict') {
    if (message.toLowerCase().includes('payload') || message.toLowerCase().includes('clientrequest')) {
      return 'idempotency-conflict';
    }
    return 'conflict';
  }
  if (code === 93003) return 'conflict';
  if (code === 93004 || key === 'travel.planning.im.unavailable') return 'unavailable';
  if (code === 92000 || code === 93000 || key.includes('validation')) return 'validation';
  return 'error';
}

export function normalizeSplitMemberBalance(value, splitCalculationId = null) {
  const netAmount = moneyToCanonicalStringSigned(value?.netAmount) || '0.00000000';
  const kind = netBalanceKind(netAmount);
  return {
    resultId: snowflakeId(value?.resultId),
    splitCalculationId: snowflakeId(value?.splitCalculationId ?? splitCalculationId),
    memberId: snowflakeId(value?.memberId),
    paidAmount: moneyToCanonicalString(value?.paidAmount) || '0.00000000',
    owedAmount: moneyToCanonicalString(value?.owedAmount) || '0.00000000',
    netAmount,
    currencyCode: String(value?.currencyCode || '').toUpperCase(),
    displayOrder: Number(value?.displayOrder) || 0,
    balanceKind: kind,
    balanceLabel: netBalanceLabel(kind),
  };
}

export function normalizeSplitCalculation(value) {
  const splitCalculationId = snowflakeId(value?.splitCalculationId);
  const memberResults = Array.isArray(value?.memberResults)
    ? value.memberResults.map((row) => normalizeSplitMemberBalance(row, splitCalculationId))
    : (Array.isArray(value?.members)
      ? value.members.map((row) => normalizeSplitMemberBalance(row, splitCalculationId))
      : []);
  const status = String(value?.status || '');
  return {
    splitCalculationId,
    journeyId: snowflakeId(value?.journeyId),
    budgetId: snowflakeId(value?.budgetId),
    calculationVersion: value?.calculationVersion == null ? null : String(value.calculationVersion),
    budgetRowVersion: value?.budgetRowVersion == null ? null : String(value.budgetRowVersion),
    budgetRowVersionUsed: value?.budgetRowVersionUsed == null ? null : String(value.budgetRowVersionUsed),
    status,
    statusLabel: splitStatusLabel(status),
    isCurrent: status === SPLIT_STATUS.CURRENT,
    isOutdated: status === SPLIT_STATUS.OUTDATED,
    calculatedAt: coerceDateTime(value?.calculatedAt),
    replay: value?.replay == null ? null : Boolean(value.replay),
    baseCurrency: String(value?.baseCurrency || '').toUpperCase(),
    totalActiveExpenseAmount: moneyToCanonicalString(value?.totalActiveExpenseAmount) || '0.00000000',
    activeExpenseCount: Number(value?.activeExpenseCount) || 0,
    memberResults,
  };
}

export function normalizeSplitMemberBalances(value) {
  const splitCalculationId = snowflakeId(value?.splitCalculationId);
  const members = Array.isArray(value?.members)
    ? value.members.map((row) => normalizeSplitMemberBalance(row, splitCalculationId))
    : [];
  const status = String(value?.status || '');
  return {
    splitCalculationId,
    journeyId: snowflakeId(value?.journeyId),
    budgetId: snowflakeId(value?.budgetId),
    calculationVersion: value?.calculationVersion == null ? null : String(value.calculationVersion),
    status,
    statusLabel: splitStatusLabel(status),
    isCurrent: status === SPLIT_STATUS.CURRENT,
    isOutdated: status === SPLIT_STATUS.OUTDATED,
    baseCurrency: String(value?.baseCurrency || '').toUpperCase(),
    totalActiveExpenseAmount: moneyToCanonicalString(value?.totalActiveExpenseAmount) || '0.00000000',
    calculatedAt: coerceDateTime(value?.calculatedAt),
    members,
  };
}

export function createSplitClientRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `split-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

export function buildCalculateSplitPayload({ expectedBudgetRowVersion, clientRequestId } = {}) {
  return {
    expectedBudgetRowVersion: Number(expectedBudgetRowVersion),
    clientRequestId: String(clientRequestId || '').trim(),
    confirm: true,
  };
}

export function canCalculateSplit({ role, lifecycleStatus }) {
  const writable = ['DRAFT', 'READY', 'TRAVELING', 'COMPLETED'].includes(lifecycleStatus);
  if (!writable) return false;
  return role === 'OWNER' || role === 'COORDINATOR' || role === 'MEMBER';
}

const SHARE_LIFECYCLES = Object.freeze(['READY', 'TRAVELING', 'COMPLETED']);

/** ACTIVE collaborator may share the latest split-balance snapshot. DRAFT cannot ensure Journey Chat. */
export function canShareBalanceToChat({ role, lifecycleStatus } = {}) {
  if (!SHARE_LIFECYCLES.includes(lifecycleStatus)) return false;
  return role === 'OWNER' || role === 'COORDINATOR' || role === 'MEMBER';
}

export const BALANCE_CHAT_SHARE_CONFIRM = '要把目前的分帳摘要分享到旅程聊天室嗎？';

export function memberDisplayLabel(member, summariesById) {
  if (!member) return '—';
  const id = snowflakeId(member.memberId);
  const roleLabel = ({
    OWNER: '擁有者',
    COORDINATOR: '協調者',
    MEMBER: '旅伴',
  })[member.role] || '';
  const role = roleLabel ? `（${roleLabel}）` : '';
  if (!id) return `—${role}`;
  // 產品顯示不外露 memberId／短碼
  return `${memberDisplayName(id, summariesById)}${role}`;
}

export {
  coerceDateTimeLocal,
  formatMoneyDisplay,
  formatSignedMoneyDisplay,
  moneyToCanonicalString,
  moneyToCanonicalStringSigned,
  netBalanceKind,
  netBalanceLabel,
  snowflakeId,
};
